import { SET_ART } from '../../gardener/sets';
import type { PaletteKey } from '../../palette';
import type { SfxName } from '../../sfx';
import type { StreakLook } from './motes';
import { POLISHED, type PolishName } from './polished';

export {
  EMBERS,
  MOTES,
  type Mote,
  type MoteMask,
  type MoteMove,
  type MoteShape,
  type MoteView,
  rampAt,
  type StreakLook,
  type StreakStyle,
  spawnMote,
  stepMote,
  viewMote,
} from './motes';

type Recolor = Partial<Record<PaletteKey, PaletteKey>>;

/**
 * A polished finish (ADR-0148) on every other gun: steel and wood onto its body's ramp, brass onto its trim's, dark to
 * light (as Gold does).
 */
const polishedPalette = ({
  body: b,
  trim: t,
}: {
  body: readonly PaletteKey[];
  trim: readonly PaletteKey[];
}): Recolor => {
  const [b0, b1, b2, b3, b4] = b as [PaletteKey, PaletteKey, PaletteKey, PaletteKey, PaletteKey];
  const [, , t2, t3, t4] = t as [PaletteKey, PaletteKey, PaletteKey, PaletteKey, PaletteKey];
  return { k: b0, '1': b0, '2': b1, '3': b2, '4': b3, '5': b4, N: b0, n: b1, m: b2, t: b3, e: b4, U: t2, Y: t3, y: t4 };
};

const POLISHED_PALETTES = Object.fromEntries(
  Object.entries(POLISHED).map(([k, p]) => [`polished_${k}`, polishedPalette(p)]),
) as Record<`polished_${PolishName}`, Recolor>;

/**
 * Gun skin palettes (ADR-0095): a skin recolours the body of every held gun, its steel (`k 1 2 3 4 5`), wood
 * (`N n m t e`) and brass (`U Y y`), each ramp onto the skin's, dark to light; its glass, vines, flames and lights
 * (greens, reds, blues, purples, orange) and its white glints stay, so each gun still reads as itself. The darkest steps
 * (`k 1 2`, `N n`) stay dark: a gun's outline is drawn in them (shared.ts `SELOUT`) and must stay under the ground's
 * value. Keyed like content `GUN_SKINS` (packages/content/src/gun-skins.ts); assets may not import content, so the keys
 * are duplicated here: keep them in sync (a client test checks). `stock` is the art as drawn. The sets' finishes
 * (ADR-0112) are drawn with their set (`SET_ART`).
 */
export const GUN_SKIN_PALETTES = {
  stock: {},
  ivory: { k: 'N', '1': 'N', '2': 'n', '3': 't', '4': 'e', '5': 'w', m: 'n', t: 'm', e: 't' },
  ember: { k: 'N', '1': 'N', '2': 'R', '3': 'r', '4': 'o', '5': 'Y', n: 'R', m: 'u', t: 'o', e: 'Y', U: 'o', Y: 'Y' },
  frost: {
    k: '1',
    '1': '1',
    '2': 'B',
    '3': 'b',
    '4': 'c',
    '5': 'w',
    N: '1',
    n: 'B',
    m: 'b',
    t: 'c',
    e: 'w',
    U: 'b',
    Y: 'c',
    y: 'w',
  },
  bubblegum: {
    k: 'N',
    '1': 'N',
    '2': 'P',
    '3': 'p',
    '4': 'i',
    '5': 'w',
    n: 'P',
    m: 'p',
    t: 'i',
    e: 'w',
    U: 'p',
    Y: 'i',
    y: 'w',
  },
  royal: { k: 'k', '1': 'k', '2': 'N', '3': 'P', '4': 'p', '5': 'i', N: 'k', n: 'N', m: 'P', t: 'p', e: 'i' },
  midnight: {
    '1': 'k',
    '2': '1',
    '3': '2',
    '4': '3',
    '5': '4',
    N: 'k',
    n: '1',
    m: '2',
    t: '3',
    e: '4',
    U: '2',
    Y: '3',
    y: '4',
  },
  gold: { k: 'N', '1': 'N', '2': 'n', '3': 'U', '4': 'Y', '5': 'y', m: 'U', t: 'Y', e: 'y', U: 'Y', Y: 'y', y: 'w' },
  gnome: SET_ART.gnome.gun,
  beekeeper: SET_ART.beekeeper.gun,
  astronaut: SET_ART.astronaut.gun,
  pumpkin: SET_ART.pumpkin.gun,
  // Effect skins (ADR-0113) paint nothing: they redraw the default gun (`pollinator_<skin>`) and leave the others stock
  // (a mythic set's, below, paints them too).
  bubble: {},
  angel: {},
  plasma: {},
  hose: {},
  // A season's finish (ADR-0123), drawn with its set.
  reaper: SET_ART.reaper.gun,
  // A creator's finish (ADR-0128, ADR-0138), drawn with the Creator set: Twitch's blue-violet.
  creator: SET_ART.creator.gun,
  // Polished skins (ADR-0148): the default gun redrawn smooth (`polished.ts`), every other gun in its finish.
  ...POLISHED_PALETTES,
  // The mythic sets' guns (ADR-0160): the default gun is each one's own sprite (`SKIN_FX`), every other gun painted in
  // the set's finish.
  inferno: SET_ART.inferno.gun,
  kitsune: SET_ART.kitsune.gun,
  voidwalker: SET_ART.voidwalker.gun,
  tempest: SET_ART.tempest.gun,
  glacier: SET_ART.glacier.gun,
  angler: SET_ART.angler.gun,
  jester: SET_ART.jester.gun,
  plague: SET_ART.plague.gun,
  chronomancer: SET_ART.chronomancer.gun,
  glitch: SET_ART.glitch.gun,
  disco: SET_ART.disco.gun,
  origami: SET_ART.origami.gun,
  ooze: SET_ART.ooze.gun,
  barnstormer: SET_ART.barnstormer.gun,
  pirate: SET_ART.pirate.gun,
  magician: SET_ART.magician.gun,
  chef: SET_ART.chef.gun,
  knight: SET_ART.knight.gun,
  sheriff: SET_ART.sheriff.gun,
  luchador: SET_ART.luchador.gun,
} as const satisfies Record<string, Recolor>;

export type GunSkinPalette = keyof typeof GUN_SKIN_PALETTES;

/**
 * How an effect skin (ADR-0113) redraws the default gun: the gun held (the Pollinator's frame, origin and grip), its
 * bullet (drawn at the stock pellet's size, over the pellet's glow tinted `glow`, its tracer in `tracer`), its muzzle
 * flash, the burst where a shot ends, the shot's sound and the sound where it ends (in place of the hit's thud).
 */
export interface SkinFx {
  held:
    | 'pollinator_bubble'
    | 'pollinator_angel'
    | 'pollinator_plasma'
    | 'pollinator_hose'
    | 'pollinator_inferno'
    | 'pollinator_kitsune'
    | 'pollinator_voidwalker'
    | 'pollinator_tempest'
    | 'pollinator_glacier'
    | 'pollinator_angler'
    | 'pollinator_jester'
    | 'pollinator_plague'
    | 'pollinator_chronomancer'
    | 'pollinator_glitch'
    | 'pollinator_disco'
    | 'pollinator_origami'
    | 'pollinator_ooze'
    | 'pollinator_barnstormer'
    | 'pollinator_pirate'
    | 'pollinator_magician'
    | 'pollinator_chef'
    | 'pollinator_knight'
    | 'pollinator_sheriff'
    | 'pollinator_luchador';
  bullet:
    | 'bubble_shot'
    | 'halo_shot'
    | 'plasma_shot'
    | 'water_shot'
    | 'hellfire_shot'
    | 'foxfire_shot'
    | 'singularity_shot'
    | 'thunderclap_shot'
    | 'permafrost_shot'
    | 'tidecaller_shot'
    | 'jackbox_shot'
    | 'censer_shot'
    | 'ticktock_shot'
    | 'overclock_shot'
    | 'boogie_shot'
    | 'fold_shot'
    | 'gloop_shot'
    | 'flak_shot'
    | 'blunder_shot'
    | 'card_shot'
    | 'pepper_shot'
    | 'bolt_shot'
    | 'star_shot'
    | 'mask_shot';
  /** A bullet that keeps its own turn (a bubble's glint stays top-left; a halo spins in its frames). */
  upright: boolean;
  muzzle:
    | 'bubble_muzzle'
    | 'angel_muzzle'
    | 'plasma_muzzle'
    | 'hose_muzzle'
    | 'hellfire_muzzle'
    | 'foxfire_muzzle'
    | 'singularity_muzzle'
    | 'thunderclap_muzzle'
    | 'permafrost_muzzle'
    | 'tidecaller_muzzle'
    | 'jackbox_muzzle'
    | 'censer_muzzle'
    | 'ticktock_muzzle'
    | 'overclock_muzzle'
    | 'boogie_muzzle'
    | 'fold_muzzle'
    | 'gloop_muzzle'
    | 'flak_muzzle'
    | 'blunder_muzzle'
    | 'card_muzzle'
    | 'pepper_muzzle'
    | 'bolt_muzzle'
    | 'star_muzzle'
    | 'smack_muzzle';
  burst:
    | 'bubble_pop'
    | 'angel_sparkle'
    | 'plasma_crackle'
    | 'water_splash'
    | 'hellfire_burst'
    | 'foxfire_burst'
    | 'singularity_burst'
    | 'thunderclap_burst'
    | 'permafrost_burst'
    | 'tidecaller_burst'
    | 'jackbox_burst'
    | 'censer_burst'
    | 'ticktock_burst'
    | 'overclock_burst'
    | 'boogie_burst'
    | 'fold_burst'
    | 'gloop_burst'
    | 'flak_burst'
    | 'blunder_burst'
    | 'card_burst'
    | 'pepper_burst'
    | 'bolt_burst'
    | 'star_burst'
    | 'pow_burst';
  sound: SfxName;
  hit: SfxName;
  tracer: number;
  glow: number;
  /** Sparks flung back where it hits, in this tint (null: none). */
  spark: number | null;
  /** Its hot streak behind its bullet (`motes.ts`, the client's `render/fx/streak.ts`), in `tracer`: its own mote. */
  streak: StreakLook;
}

const GOLD = 0xfee761;

/**
 * The effect skins' art and sounds (ADR-0113), keyed like content's effect skins (the client's `skinFx` checks the keys);
 * the game's juice and the admin app's Skins page (ADR-0157) both draw from it.
 */
export const SKIN_FX = {
  bubble: {
    held: 'pollinator_bubble',
    bullet: 'bubble_shot',
    upright: true,
    muzzle: 'bubble_muzzle',
    burst: 'bubble_pop',
    sound: 'blup',
    hit: 'bubblePop',
    tracer: 0x9be8ff,
    glow: 0x9be8ff,
    spark: null,
    streak: {
      ramp: [0xffffff, 0x9be8ff, 0x2ce8f5, 0x0099db],
      mote: 'bubble',
      move: 'float',
      rise: 1.6,
      life: 2.4,
      size: 2.2,
      rate: 0.7,
    },
  },
  angel: {
    held: 'pollinator_angel',
    bullet: 'halo_shot',
    upright: true,
    muzzle: 'angel_muzzle',
    burst: 'angel_sparkle',
    sound: 'choir',
    hit: 'twinkle',
    tracer: GOLD,
    glow: 0xfeae34,
    spark: 0xfee761,
    streak: {
      ramp: [0xffffff, 0xead4aa, 0xfee761],
      mote: 'feather',
      move: 'flutter',
      fall: 28,
      scatter: 0.8,
      life: 2.4,
      size: 3.4,
      rate: 0.8,
    },
  },
  plasma: {
    held: 'pollinator_plasma',
    bullet: 'plasma_shot',
    upright: false,
    muzzle: 'plasma_muzzle',
    burst: 'plasma_crackle',
    sound: 'plasmaZap',
    hit: 'plasmaCrackle',
    tracer: 0x2ce8f5,
    glow: 0x2ce8f5,
    spark: 0x2ce8f5,
    streak: {
      ramp: [0xffffff, 0x2ce8f5, 0x0099db, 0x124e89],
      mote: 'orb',
      scatter: 2.2,
      rise: 0,
      fall: 0,
      life: 0.9,
      size: 1.9,
      rate: 2.2,
    },
  },
  hose: {
    held: 'pollinator_hose',
    bullet: 'water_shot',
    upright: false,
    muzzle: 'hose_muzzle',
    burst: 'water_splash',
    sound: 'bloop',
    hit: 'drips',
    tracer: 0x9be8ff,
    glow: 0x0099db,
    spark: null,
    streak: {
      ramp: [0xffffff, 0x9be8ff, 0x2ce8f5, 0x0099db],
      mote: 'drop',
      move: 'drip',
      fall: 320,
      scatter: 0.5,
      life: 1.3,
      size: 1.9,
      rate: 2,
    },
  },
  // The mythic sets' guns (ADR-0160).
  inferno: {
    held: 'pollinator_inferno',
    bullet: 'hellfire_shot',
    upright: false,
    muzzle: 'hellfire_muzzle',
    burst: 'hellfire_burst',
    sound: 'hellfireShot',
    hit: 'hellfireHit',
    tracer: 0xf77622,
    glow: 0xf77622,
    spark: 0xfeae34,
    streak: {
      ramp: [0xffffff, 0xfee761, 0xfeae34, 0xf77622, 0xe43b44, 0xa22633],
      mote: 'flame',
      rise: 2.4,
      fall: 0,
      life: 1.5,
      size: 2.4,
      rate: 2.2,
    },
  },
  kitsune: {
    held: 'pollinator_kitsune',
    bullet: 'foxfire_shot',
    upright: false,
    muzzle: 'foxfire_muzzle',
    burst: 'foxfire_burst',
    sound: 'foxfireShot',
    hit: 'foxfireHit',
    tracer: 0xf6757a,
    glow: 0xf6757a,
    spark: null,
    streak: {
      ramp: [0xffffff, 0xf6757a, 0xb55088, 0x68386c],
      mote: 'wisp',
      move: 'helix',
      blink: true,
      rise: 0.8,
      scatter: 0.9,
      life: 2,
      size: 4.5,
      rate: 1.3,
    },
  },
  voidwalker: {
    held: 'pollinator_voidwalker',
    bullet: 'singularity_shot',
    upright: true,
    muzzle: 'singularity_muzzle',
    burst: 'singularity_burst',
    sound: 'voidShot',
    hit: 'voidHit',
    tracer: 0xb55088,
    glow: 0xb55088,
    spark: 0xf6757a,
    streak: {
      ramp: [0xffffff, 0xf6757a, 0xb55088, 0x68386c],
      mote: 'vortex',
      move: 'implode',
      life: 1.3,
      size: 4.4,
      rate: 1.1,
      tip: 0xf6757a,
    },
  },
  tempest: {
    held: 'pollinator_tempest',
    bullet: 'thunderclap_shot',
    upright: false,
    muzzle: 'thunderclap_muzzle',
    burst: 'thunderclap_burst',
    sound: 'thunderShot',
    hit: 'thunderHit',
    tracer: 0xfee761,
    glow: 0xfee761,
    spark: 0xfee761,
    streak: {
      ramp: [0xffffff, 0xfee761, 0x2ce8f5],
      mote: 'bolt',
      move: 'flash',
      scatter: 1.4,
      life: 0.5,
      size: 3.6,
      rate: 1.4,
    },
  },
  // The mythic sets after the first four (ADR-0160 amended).
  glacier: {
    held: 'pollinator_glacier',
    bullet: 'permafrost_shot',
    upright: false,
    muzzle: 'permafrost_muzzle',
    burst: 'permafrost_burst',
    sound: 'permafrostShot',
    hit: 'permafrostHit',
    tracer: 0xc0cbdc,
    glow: 0x2ce8f5,
    spark: 0xffffff,
    streak: {
      ramp: [0xffffff, 0x9be8ff, 0x2ce8f5, 0x0099db],
      mote: 'flake',
      rise: 0,
      fall: 22,
      scatter: 0.7,
      life: 2.4,
      size: 3.2,
      rate: 1.2,
    },
  },
  angler: {
    held: 'pollinator_angler',
    bullet: 'tidecaller_shot',
    upright: false,
    muzzle: 'tidecaller_muzzle',
    burst: 'tidecaller_burst',
    sound: 'tidecallerShot',
    hit: 'tidecallerHit',
    tracer: 0x63c74d,
    glow: 0x63c74d,
    spark: 0xffffff,
    streak: {
      ramp: [0xffffff, 0x9be8ff, 0x63c74d, 0x3e8948],
      mote: 'ripple',
      move: 'expand',
      scatter: 0.6,
      life: 1.1,
      size: 5,
      rate: 0.8,
    },
  },
  jester: {
    held: 'pollinator_jester',
    bullet: 'jackbox_shot',
    upright: false,
    muzzle: 'jackbox_muzzle',
    burst: 'jackbox_burst',
    sound: 'jackboxShot',
    hit: 'jackboxHit',
    tracer: 0xfee761,
    glow: 0xfeae34,
    spark: 0xb55088,
    streak: {
      ramp: [0xfee761, 0xb55088, 0x2ce8f5, 0xe43b44, 0x63c74d],
      mote: 'diamond',
      move: 'pop',
      mix: true,
      burst: 6,
      fall: 170,
      life: 1.8,
      size: 2.8,
      rate: 0.35,
    },
  },
  plague: {
    held: 'pollinator_plague',
    bullet: 'censer_shot',
    upright: false,
    muzzle: 'censer_muzzle',
    burst: 'censer_burst',
    sound: 'censerShot',
    hit: 'censerHit',
    tracer: 0xb55088,
    glow: 0xb55088,
    spark: 0xc0cbdc,
    streak: {
      ramp: [0xc0cbdc, 0xb55088, 0x68386c, 0x3a4466],
      mote: 'puff',
      move: 'smoke',
      rise: 1,
      scatter: 0.5,
      life: 2.6,
      size: 4.6,
      rate: 1.1,
    },
  },
  chronomancer: {
    held: 'pollinator_chronomancer',
    bullet: 'ticktock_shot',
    upright: false,
    muzzle: 'ticktock_muzzle',
    burst: 'ticktock_burst',
    sound: 'ticktockShot',
    hit: 'ticktockHit',
    tracer: 0xfeae34,
    glow: 0xfeae34,
    spark: 0xfee761,
    streak: {
      ramp: [0xffffff, 0xfee761, 0xfeae34, 0xb86f50],
      mote: 'gear',
      move: 'hover',
      fall: 0,
      scatter: 0.6,
      life: 2.2,
      size: 3.2,
      rate: 1,
    },
  },
  glitch: {
    held: 'pollinator_glitch',
    bullet: 'overclock_shot',
    upright: false,
    muzzle: 'overclock_muzzle',
    burst: 'overclock_burst',
    sound: 'overclockShot',
    hit: 'overclockHit',
    tracer: 0x63c74d,
    glow: 0x63c74d,
    spark: 0xb55088,
    streak: {
      ramp: [0x63c74d, 0xb55088, 0x2ce8f5, 0xffffff],
      mote: 'pixel',
      move: 'jitter',
      mix: true,
      blink: true,
      scatter: 0.8,
      life: 1.1,
      size: 1.7,
      rate: 2.2,
    },
  },
  disco: {
    held: 'pollinator_disco',
    bullet: 'boogie_shot',
    upright: true,
    muzzle: 'boogie_muzzle',
    burst: 'boogie_burst',
    sound: 'boogieShot',
    hit: 'boogieHit',
    tracer: 0xf6757a,
    glow: 0xf6757a,
    spark: 0xfee761,
    streak: {
      ramp: [0xf6757a, 0xfee761, 0x2ce8f5, 0x63c74d, 0xb55088],
      mote: 'note',
      move: 'float',
      mix: true,
      rise: 0.9,
      life: 2.2,
      size: 3.4,
      rate: 0.9,
    },
  },
  origami: {
    held: 'pollinator_origami',
    bullet: 'fold_shot',
    upright: false,
    muzzle: 'fold_muzzle',
    burst: 'fold_burst',
    sound: 'foldShot',
    hit: 'foldHit',
    tracer: 0xead4aa,
    glow: 0xffffff,
    spark: 0xe43b44,
    streak: {
      ramp: [0xffffff, 0xead4aa, 0xe43b44],
      mote: 'paper',
      move: 'flutter',
      mix: true,
      fall: 34,
      life: 2.4,
      size: 2.8,
      rate: 0.8,
    },
  },
  ooze: {
    held: 'pollinator_ooze',
    bullet: 'gloop_shot',
    upright: false,
    muzzle: 'gloop_muzzle',
    burst: 'gloop_burst',
    sound: 'gloopShot',
    hit: 'gloopHit',
    tracer: 0xb55088,
    glow: 0xf6757a,
    spark: 0xffffff,
    streak: {
      ramp: [0xffffff, 0xf6757a, 0xb55088, 0x68386c],
      mote: 'blob',
      move: 'drip',
      fall: 110,
      scatter: 0.6,
      life: 1.6,
      size: 2.4,
      rate: 1.2,
    },
  },
  barnstormer: {
    held: 'pollinator_barnstormer',
    bullet: 'flak_shot',
    upright: false,
    muzzle: 'flak_muzzle',
    burst: 'flak_burst',
    sound: 'flakShot',
    hit: 'flakHit',
    tracer: 0xfee761,
    glow: 0xfeae34,
    spark: 0xf77622,
    streak: {
      ramp: [0xffffff, 0xc0cbdc, 0x8b9bb4],
      mote: 'cloud',
      move: 'expand',
      scatter: 0.5,
      life: 1.5,
      size: 6,
      rate: 1,
    },
  },
  pirate: {
    held: 'pollinator_pirate',
    bullet: 'blunder_shot',
    upright: false,
    muzzle: 'blunder_muzzle',
    burst: 'blunder_burst',
    sound: 'blunderShot',
    hit: 'blunderHit',
    tracer: 0xc0cbdc,
    glow: 0xfeae34,
    spark: 0xb86f50,
    streak: {
      ramp: [0xfee761, 0xfeae34],
      mote: 'coin',
      move: 'arc',
      mix: true,
      rise: 1.2,
      fall: 260,
      life: 1.6,
      size: 3,
      rate: 0.9,
    },
  },
  magician: {
    held: 'pollinator_magician',
    bullet: 'card_shot',
    upright: true,
    muzzle: 'card_muzzle',
    burst: 'card_burst',
    sound: 'cardShot',
    hit: 'cardHit',
    tracer: 0xfee761,
    glow: 0xffffff,
    spark: 0xfee761,
    streak: {
      ramp: [0xffffff, 0xfee761],
      mote: 'sparkle',
      move: 'hover',
      blink: true,
      fall: 0,
      life: 1.8,
      size: 3.2,
      rate: 1.2,
    },
  },
  chef: {
    held: 'pollinator_chef',
    bullet: 'pepper_shot',
    upright: false,
    muzzle: 'pepper_muzzle',
    burst: 'pepper_burst',
    sound: 'pepperShot',
    hit: 'pepperHit',
    tracer: 0xf77622,
    glow: 0xe43b44,
    spark: 0xfeae34,
    streak: {
      ramp: [0xe43b44, 0xf77622, 0x733e39, 0x3e2731],
      mote: 'speck',
      move: 'arc',
      mix: true,
      rise: 0.3,
      fall: 140,
      life: 1.4,
      size: 1.5,
      rate: 3.4,
    },
  },
  knight: {
    held: 'pollinator_knight',
    bullet: 'bolt_shot',
    upright: false,
    muzzle: 'bolt_muzzle',
    burst: 'bolt_burst',
    sound: 'boltShot',
    hit: 'boltHit',
    tracer: 0xc0cbdc,
    glow: 0xfee761,
    spark: 0xffffff,
    streak: {
      ramp: [0xffffff, 0xc0cbdc, 0xfee761, 0xfeae34],
      mote: 'spark',
      move: 'arc',
      rise: 0.8,
      fall: 300,
      scatter: 1.6,
      life: 0.7,
      size: 1.6,
      rate: 2,
    },
  },
  sheriff: {
    held: 'pollinator_sheriff',
    bullet: 'star_shot',
    upright: true,
    muzzle: 'star_muzzle',
    burst: 'star_burst',
    sound: 'starShot',
    hit: 'starHit',
    tracer: 0xfee761,
    glow: 0xfeae34,
    spark: 0xead4aa,
    streak: {
      ramp: [0xfee761, 0xfeae34],
      mote: 'star5',
      mix: true,
      rise: 0,
      fall: 30,
      scatter: 0.9,
      life: 1.8,
      size: 3,
      rate: 1,
    },
  },
  luchador: {
    held: 'pollinator_luchador',
    bullet: 'mask_shot',
    upright: true,
    muzzle: 'smack_muzzle',
    burst: 'pow_burst',
    sound: 'slapShot',
    hit: 'bellHit',
    tracer: 0xb55088,
    glow: 0xfee761,
    spark: 0xf77622,
    streak: {
      ramp: [0xb55088, 0xfee761, 0xf77622, 0x2ce8f5],
      mote: 'ribbon',
      move: 'flutter',
      mix: true,
      fall: 40,
      life: 2.2,
      size: 4,
      rate: 1,
    },
  },
} as const satisfies Record<string, SkinFx>;

/** The glow under an effect skin's bullet and at its muzzle: white rings, which take the skin's cool tints. */
export const SKIN_GLOW = 'skin_glow/idle/0';
